// Studio helper for the 4 regional user-uploads buckets (same mapping as the editor's
// optimize-url toUploadsCdnUrl). Raw S3 reads of these buckets are not public, so a raw
// URL stored in an old project only loads on its CDN host. Only the host is swapped:
// the S3 object key is never changed.

// Rollout switch: raw uploads URLs are read on their CDN host (unchanged while off).
export const UPLOADS_CDN_REWRITE = true;

// The legacy `invideo-uploads` and `invideo-uploads-videos` buckets are out of scope.
const UPLOADS_BUCKETS: Record<string, { region: string; cdnHost: string }> = {
  'invideo-uploads-ap-south-1': { region: 'ap-south-1', cdnHost: 'user-uploads-aps1.invideo.io' },
  'invideo-uploads-us-east-1': { region: 'us-east-1', cdnHost: 'd1kri5mif3adym.cloudfront.net' },
  'invideo-uploads-ap-southeast-1': {
    region: 'ap-southeast-1',
    cdnHost: 'de298hc1e0fzm.cloudfront.net',
  },
  'invideo-uploads-eu-west-2': { region: 'eu-west-2', cdnHost: 'd2uvx3ocukou0h.cloudfront.net' },
};

// every CDN host of the 4 upload buckets -> its bucket (d1c7ic18uy0m4b is the ap-south-1
// CloudFront id, user-uploads-aps1.invideo.io its alias)
const UPLOADS_CDN_HOST_BUCKETS: Record<string, string> = Object.keys(UPLOADS_BUCKETS).reduce(
  (hosts, bucket) => ({ ...hosts, [UPLOADS_BUCKETS[bucket].cdnHost]: bucket }),
  { 'd1c7ic18uy0m4b.cloudfront.net': 'invideo-uploads-ap-south-1' } as Record<string, string>,
);
const UPLOADS_CDN_HOSTS = Object.keys(UPLOADS_CDN_HOST_BUCKETS);
// resized copies: <N>x<N>/<file> and previews-480/<file>
const RESIZED_UPLOADS_PATH = /^\/(\d+x\d+|previews-480)\//;

// S3 signing params (expired on stored URLs); CloudFront reads Signature/Expires/Policy/
// Key-Pair-Id as its own signed-URL params and rejects the request.
const SIGNING_QUERY = ['awsaccesskeyid', 'signature', 'expires', 'policy', 'key-pair-id'];

/** A raw S3 query without its signing params (also used by ./lockedBucketsCdn). */
export function unsignedSearch(search: string): string {
  const kept = search
    .replace(/^\?/, '')
    .split('&')
    .filter((part) => {
      if (!part) {
        return false;
      }
      const name = part.split('=')[0].toLowerCase();
      return !name.startsWith('x-amz-') && !SIGNING_QUERY.includes(name);
    });
  return kept.length ? `?${kept.join('&')}` : '';
}

function uploadsBucketAndKey(parsed: URL): [string, string] | null {
  const host = parsed.hostname.toLowerCase();
  for (const bucket of Object.keys(UPLOADS_BUCKETS)) {
    const { region } = UPLOADS_BUCKETS[bucket];
    if (
      host === `${bucket}.s3.${region}.amazonaws.com` ||
      host === `${bucket}.s3-${region}.amazonaws.com` ||
      host === `${bucket}.s3.amazonaws.com`
    ) {
      return [bucket, parsed.pathname.replace(/^\//, '')];
    }
    if (
      (host === `s3.${region}.amazonaws.com` ||
        host === `s3-${region}.amazonaws.com` ||
        host === 's3.amazonaws.com') &&
      parsed.pathname.startsWith(`/${bucket}/`)
    ) {
      return [bucket, parsed.pathname.slice(bucket.length + 2)];
    }
  }
  return null;
}

/**
 * Maps a raw S3 URL of one of the 4 regional upload buckets (virtual-host or
 * path-style, exact hosts only) to its CDN host. Anything else, CDN URLs included,
 * is returned unchanged. Pure string work: never fetches. Path and query are kept
 * as is (a raw '+' becomes %20, S3 signing params are dropped), so already encoded
 * keys are not re-encoded.
 */
export function toUploadsCdnUrl<T extends string | null | undefined>(url: T): T {
  if (!UPLOADS_CDN_REWRITE || typeof url !== 'string' || !url.includes('amazonaws.com')) {
    return url;
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch (e) {
    return url;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return url;
  }
  const media = uploadsBucketAndKey(parsed);
  if (!media || !media[1]) {
    return url;
  }
  // S3 REST reads a raw '+' in the path as a space; the CDN needs it as %20 (%2B stays)
  const key = media[1].replace(/\+/g, '%20');
  return `https://${UPLOADS_BUCKETS[media[0]].cdnHost}/${key}${unsignedSearch(parsed.search)}` as T;
}

/** True for a resized copy (`<N>x<N>/`, `previews-480/`) on an upload bucket CDN host. */
export function isResizedUploadsCdnUrl(url: unknown): boolean {
  if (typeof url !== 'string') {
    return false;
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch (e) {
    return false;
  }
  return (
    parsed.protocol === 'https:' &&
    UPLOADS_CDN_HOSTS.includes(parsed.hostname.toLowerCase()) &&
    RESIZED_UPLOADS_PATH.test(parsed.pathname)
  );
}

/** [bucket, region] of an upload bucket CDN host (exact, lowercase host); null for any other host. */
export function uploadsBucketOfCdnHost(host: string): [string, string] | null {
  const bucket = UPLOADS_CDN_HOST_BUCKETS[host];
  return bucket ? [bucket, UPLOADS_BUCKETS[bucket].region] : null;
}
